import React from 'react';
import {
  FileJson,
  Clock,
  Users,
  BookOpen,
  ScanLine,
  Bell,
  Download,
  RotateCcw,
  Trash2,
} from 'lucide-react';

export interface BackupFileItem {
  filename: string;
  sizeBytes: number;
  createdAt: string;
  counts?: {
    users: number;
    books: number;
    scans: number;
    notifications: number;
    posts?: number;
    blogCategories?: number;
    systemSettings?: number;
  };
}

interface BackupItemRowProps {
  backup: BackupFileItem;
  isDeleting: boolean;
  onInitiateRestore: (filename: string) => void;
  onInitiateDelete: (filename: string) => void;
}

export const BackupItemRow: React.FC<BackupItemRowProps> = ({
  backup,
  isDeleting,
  onInitiateRestore,
  onInitiateDelete,
}) => {
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('id-ID', {
        dateStyle: 'medium',
        timeStyle: 'medium',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-4 sm:p-5 hover:bg-slate-50/80 transition flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="space-y-1.5 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <FileJson className="w-4 h-4 text-indigo-600 shrink-0" />
          <span className="font-mono text-xs sm:text-sm font-bold text-slate-900 truncate">
            {backup.filename}
          </span>
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
            {formatBytes(backup.sizeBytes)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            {formatDate(backup.createdAt)}
          </span>

          {backup.counts && (
            <div className="flex flex-wrap items-center gap-2.5 text-[11px] font-medium text-slate-600 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200/70">
              <span className="flex items-center gap-1" title="Pengguna Terdaftar">
                <Users className="w-3 h-3 text-indigo-600" /> {backup.counts.users}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1" title="Judul Buku">
                <BookOpen className="w-3 h-3 text-emerald-600" /> {backup.counts.books}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1" title="Pemindaian">
                <ScanLine className="w-3 h-3 text-amber-600" /> {backup.counts.scans}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1" title="Notifikasi">
                <Bell className="w-3 h-3 text-blue-600" /> {backup.counts.notifications}
              </span>
              {typeof backup.counts.posts === 'number' && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1" title="Artikel Blog">
                    <span className="font-bold text-purple-600">Post:</span> {backup.counts.posts}
                  </span>
                </>
              )}
              {typeof backup.counts.systemSettings === 'number' && backup.counts.systemSettings > 0 && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1" title="Konfigurasi Sistem">
                    <span className="font-bold text-slate-600">Config:</span> {backup.counts.systemSettings}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 w-full sm:w-auto justify-end">
        <a
          href={`/api/admin/database/backup/download/${encodeURIComponent(backup.filename)}`}
          download={backup.filename}
          className="px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs flex items-center gap-1.5 transition border border-indigo-200"
          title="Unduh berkas cadangan ke komputer"
        >
          <Download className="w-3.5 h-3.5" />
          <span>Unduh</span>
        </a>

        {backup.filename.endsWith('.json') && (
          <button
            type="button"
            onClick={() => onInitiateRestore(backup.filename)}
            className="px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 font-bold text-xs flex items-center gap-1.5 transition border border-amber-200 cursor-pointer"
            title="Pulihkan database dari berkas ini"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Restore</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => onInitiateDelete(backup.filename)}
          disabled={isDeleting}
          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition cursor-pointer"
          title="Hapus backup ini"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
